import React, { useContext } from 'react';
import { Link } from 'react-router-dom';

import ChevronLeft from '@mui/icons-material/ChevronLeft';
import Close from '@mui/icons-material/Close';
import Done from '@mui/icons-material/Done';

import { getApplicationEndPoint } from '../../common/api.endPoints';
import { ApplicationDetailedLifecycle } from '../../common/components/ApplicationDetailedLifecycle';
import { ApplicationLifecycle } from '../../common/components/ApplicationLifecycle';
import { ApplicationStageV2 } from '../../common/components/ApplicationStageV2';
import { SlaCountdown } from '../../common/components/SlaCountdown';
import { Tag } from '../../common/components/Tag';
import { DateTimeFormats } from '../../common/constants/dateFormats';
import { Decision } from '../../common/constants/decision';
import { getCarrierRole } from '../../common/constants/roles';
import { calculateSla, getFormattedTimestamp } from '../../common/helpers';
import { ProductFactory } from '../../common/productConfig/productFactory';
import { TColumnsResolver } from '../../common/types/TColumnsResolver';
import { AuthContext } from '../login/AuthWrapper';
import { IApplicationRow } from './interfaces/IApplicationRow';
import styles from './styles.module.scss';

/**
 * The columns resolver is passed into the Grid and is called
 * before any default fallback cell creation happens. This allows us
 * to do any customized checks here instead of in the core grid view.
 */
const columnsResolver: TColumnsResolver = (
  col: any,
  row: any,
): string | JSX.Element | undefined => {
  if (!col || !row) return undefined; // Safeguard for invalid inputs

  let cellValue;
  const auth = useContext(AuthContext);
  const isCarrierRole = getCarrierRole(auth?.roles);

  switch (col.name) {
    case 'stage':
      cellValue = <ApplicationStageV2 stage={row.stageV2} />;
      break;
    case 'lifecycle':
      cellValue = <ApplicationLifecycle lifecycle={row.lifecycleV2} />;
      break;
    case 'detailedLifecycle':
      cellValue = (
        <ApplicationDetailedLifecycle
          detailedLifecycle={row.detailedLifecycle}
        />
      );
      break;
    case 'creationDate':
      cellValue = (
        <>
          <span>
            {getFormattedTimestamp(
              row.creationDate,
              DateTimeFormats.SIMPLE_DATE,
            )}
          </span>
          <span className={styles.CreatedTime}>
            {getFormattedTimestamp(
              row.creationDate,
              DateTimeFormats.TIME_AM_PM,
            )}
          </span>
        </>
      );
      break;
    case 'appliedFor':
      cellValue = getAppliedFor(row);
      break;
    case 'forReview':
      cellValue = getTasks(row);
      break;
    case 'sla':
      cellValue = getSla(row);
      break;
    case 'applicationSla':
      cellValue = <SlaCountdown sla={row.stageSla} />;
      break;
    case 'lastDecisionType':
      cellValue = getLastDecisionType(row);
      break;
    case 'customerDisplayName':
      cellValue = getCustomerName(row.id, row.customerDisplayName);
      break;
    case 'product':
      cellValue = getProduct(row);
      break;
    case 'underwriterDisplayName':
      const name = isCarrierRole
        ? row.carrierUnderwriterDisplayName
        : row.underwriterDisplayName;
      cellValue = getAssignerName(name);
      break;
    case 'caseManagerDisplayName':
      cellValue = getAssignerName(row.caseManagerDisplayName);
      break;
    case 'agencyName':
      cellValue = row.agencyName ? row.agencyName : 'N/A';
      break;
    default:
      // If no conditions above catch, returns undefined which results in the
      // grid's default cell value being used as a fallback.
      cellValue = undefined;
  }

  return cellValue;
};

export default columnsResolver;

/**
 * Generates the SLA tag.
 *
 * @param {IApplicationRow} row - display formatted coverage.
 */
const getSla = (row: IApplicationRow) => {
  if (!row.sla) {
    return;
  }

  const sla = calculateSla(new Date(row.sla));
  const color = sla >= 16 ? 'Green' : sla >= 0 ? 'Orange' : 'Red';
  return <Tag color={color}>{sla.toFixed(0) + 'h'}</Tag>;
};

/**
 * Generates the applied value to be rendered.
 *
 * @param {IApplicationRow} row - display formatted coverage.
 */
const getAppliedFor = (row: IApplicationRow) => {
  return row.coverage != null ? `$${row.coverage.toLocaleString()}` : '—';
};

/**
 * Generates the decision type value to be rendered.
 *
 * @param {IApplicationRow} row - display formatted decision type.
 */
const getTasks = (row: IApplicationRow) => {
  const taskLabels: { [key: string]: string } = {
    RX: 'Rx History',
    MIB: 'MIB Report',
    DISCLOSURE: 'Disclosure',
    LETTER: 'Letter',
    ORDER_MIB_CODEBACK: 'Order Codebacks',
    MIB_CODEBACK: 'Codebacks Error',
    OTAF_LETTER: 'OTAF Letter',
    DECLINE_LETTER: 'Decline Letter',
    MEDEXAM: 'Medexam',
    LAB_PIQTURE: 'Lab PiQture',
    MEDICAL_CLAIMS: 'Medical Claims',
    SCRIPT_CHECK: 'Script Check',
    MIB_CODE_DETAILS: 'MIB Code Details',
    INSURANCE_LABS: 'Insurance Labs',
    CRIMINAL_RECORDS: 'Criminal Records',
    ADDITIONAL_INFORMATION_FROM_CUSTOMER: 'Info From Customer',
    AUTO_MEDICAL_CLAIMS: 'Automated Medical Claims',
    RUW_WITHDRAWL_LETTER: 'Withdrawal Letter',
    DISPUTE: 'Dispute',
  };

  const renderTasks = () => {
    if (row.forReview.length === 0) return null;

    const firstTask = (
      <span key={row.forReview[0].type + '0'} className={styles.TaskTag}>
        <Tag color="Gray">
          {taskLabels[row.forReview[0].type] || row.forReview[0].type}
        </Tag>
      </span>
    );

    const additionalTasksCount = row.forReview.length - 1;
    const moreIndicator =
      additionalTasksCount > 0 ? (
        <span key="more-indicator" className={styles.MoreTag}>
          <Tag color="Gray">{`+ ${additionalTasksCount.toString()}`}</Tag>
        </span>
      ) : null;

    return (
      <>
        {firstTask}
        {moreIndicator}
      </>
    );
  };

  return <div>{renderTasks()}</div>;
};

/**
 * Generates the decision type value to be rendered.
 *
 * @param {IApplicationRow} row - display formatted decision type.
 */
const getLastDecisionType = (row: IApplicationRow) => {
  return lastDecisionType(row.lastDecisionType);
};

const getProduct = (row: IApplicationRow) => {
  const productConfig = ProductFactory.getProductConfig(
    row.carrier,
    row.product,
    row.subCategory ?? '',
  );
  const defaultLabel = `${row.carrier} ${row.product}`;
  return productConfig?.getProductDisplayText() ?? defaultLabel;
};

export const lastDecisionType = (lastDecision: string) => {
  let icon;
  const iconClass = [styles.DecisionType];
  if (lastDecision === Decision.APPROVED) {
    icon = <Done sx={{ width: '14px', height: '14px' }} />;
    iconClass.push(styles.ApprovedDecision);
  } else if (lastDecision === Decision.DECLINED) {
    icon = <Close sx={{ width: '14px', height: '14px' }} />;
    iconClass.push(styles.DeclinedDecision);
  } else if (lastDecision === Decision.WITHDRAWN) {
    icon = <ChevronLeft sx={{ width: '18px', height: '18px' }} />;
    iconClass.push(styles.WithdrawnDecision);
  }

  return <span className={iconClass.join(' ')}>{icon}</span>;
};

/**
 * Generates the customer name to be rendered.
 *
 * @param {number} rowId - the ID of current row so we can build a link
 * @param {string} customerDisplayName - the custom name displayed inside the
 * hyperlink to the application page.
 */
const getCustomerName = (rowId: number, customerDisplayName: string) => {
  return (
    <Link
      to={`/workbench/${getApplicationEndPoint(String(rowId))}`}
      target="_blank"
    >
      {customerDisplayName}
    </Link>
  );
};

const getAssignerName = (name: any) => {
  if (name === 'null null(Inactive)') {
    return '-';
  }
  return name;
};
